import React, { FC, ReactNode } from "react";
import { MatterEditorPricingSummary, MatterEditorPricing, VpdScrollable } from "../../components";

import {
  PricingType,
  PricingOptions,
  GpmThreshold,
  OnPriceChange,
  Matter,
  PricingLevel,
  PriceRange,
  MatterTemplate,
  OnPriceRangeChange,
} from "../../data-types";

import {
  sumMatterWorkPriceSingle,
  sumMatterWorkPriceRange,
  sumMatterCostRange,
  sumMatterCostSingle,
  calculateGpm,
  calculateGpmRange,
} from "./sum-legal-fee-costs";
import { TaxLabel } from "~/gql/graphql";
type PricingSidebarProps = {
  pricingSummaries: PricingOptions;
  gpmThreshold: GpmThreshold;
  taxLevelPercent: number;
  excludeTax: boolean;
  footer: ReactNode;
  onChosenPriceChange: OnPriceChange;
  onChosenPriceRangeChange: OnPriceRangeChange;
  matter: Matter | MatterTemplate;
  isTemplate: boolean;
  onPhasePriceChange: (phaseId: number, priceDetails: Partial<PricingOptions>) => void;
  onFixedPriceAlternativeChange?: OnPriceChange;
  fixedPriceAlternative?: number | null;
  currencySymbol: string;
  taxLabel: TaxLabel;
  subTotalExcludingVat: number | { lower: number; upper: number };
  legalFees: number | { lower: number; upper: number };
  administrationCharge: number;
  onWaiveAdminChargeChange: (value: boolean, reason?: string) => void;
  useTechPricing: boolean;
};

const canDisplayCalculatedPrice = (pricingLevel: PricingLevel, pricingType: PricingType): boolean =>
  pricingLevel === PricingLevel.FeeEarner || pricingType === PricingType.TopDown;

export const PricingSidebar: FC<PricingSidebarProps> = ({
  pricingSummaries,
  gpmThreshold,
  footer,
  onChosenPriceChange,
  matter,
  isTemplate,
  onPhasePriceChange,
  onChosenPriceRangeChange,
  taxLevelPercent,
  excludeTax,
  onFixedPriceAlternativeChange,
  fixedPriceAlternative,
  currencySymbol,
  taxLabel,
  subTotalExcludingVat,
  legalFees,
  administrationCharge,
  onWaiveAdminChargeChange,
  useTechPricing,
}) => {
  if (matter.pricingType === PricingType.None) return null;

  const isFeeRange = matter.pricingType === PricingType.EstimatedRange;
  const suggestedPrice = canDisplayCalculatedPrice(matter.pricingLevel, matter.pricingType)
    ? isFeeRange
      ? sumMatterWorkPriceRange(
          matter.phases,
          matter.work,
          matter.priceOptions?.topDownRateDiscount
        )
      : sumMatterWorkPriceSingle(
          matter.phases,
          matter.work,
          matter.priceOptions?.topDownRateDiscount
        )
    : null;

  const cost = isFeeRange
    ? sumMatterCostRange(matter.phases, matter.work, matter.pricingLevel, matter.pricingType)
    : sumMatterCostSingle(matter.phases, matter.work, matter.pricingLevel, matter.pricingType);

  const uplifts = (matter as Matter)?.uplifts ? (matter as Matter)!.uplifts : [];
  const discounts = (matter as Matter)?.discounts ? (matter as Matter)!.discounts : [];

  const gpm = isFeeRange
    ? calculateGpmRange(cost as PriceRange, subTotalExcludingVat as PriceRange)
    : calculateGpm(cost as number, subTotalExcludingVat as number);

  const hasFeeEarnerLevelPricing =
    matter.phases?.flatMap((x) => x.work).some((w) => !w?.feeEarner && !w?.feeEarnerId) ?? false;

  return (
    <VpdScrollable>
      <MatterEditorPricingSummary
        isFeeEarnerLevelGpm={hasFeeEarnerLevelPricing}
        currencySymbol={currencySymbol}
        gpmThreshold={gpmThreshold}
        onChosenPriceChange={onChosenPriceChange}
        pricingType={matter.pricingType}
        gpm={gpm}
        suggestedPrice={suggestedPrice}
        chosenPrice={legalFees}
        pricingLevel={matter.pricingLevel}
        onFixedPriceAlternativeChange={(val) => {
          onFixedPriceAlternativeChange?.(val);
        }}
        onChosenRangePriceChange={onChosenPriceRangeChange}
        fixedPriceAlternative={fixedPriceAlternative}
        matterDetails={{
          title: matter.title,
          externalReference: (matter as Matter)?.externalReference ?? "",
          client: (matter as Matter)?.client?.name,
          clientRef: (matter as Matter)?.client?.externalReference,
        }}
      />
      <MatterEditorPricing
        taxLabel={taxLabel}
        currencySymbol={currencySymbol}
        template={isTemplate}
        phases={matter.phases}
        discounts={discounts}
        disbursements={matter.disbursements}
        uplifts={uplifts}
        technologyCharges={matter.technologyCharges}
        matterWork={matter.work}
        topDownRateDiscount={matter.priceOptions?.topDownRateDiscount ?? 0}
        pricingOptions={pricingSummaries}
        pricingType={matter.pricingType}
        pricingLevel={matter.pricingLevel}
        onUpdatePrice={onPhasePriceChange}
        footer={footer}
        taxLevelPercent={taxLevelPercent}
        administrationCharge={administrationCharge}
        waiveAdminCharge={matter.waiveAdminCharge ?? false}
        waiveAdminChargeReason={matter.waiveAdminChargeReason ?? null}
        onWaiveAdminChargeChange={onWaiveAdminChargeChange}
        excludeTax={excludeTax}
        useTechPricing={useTechPricing}
      />
    </VpdScrollable>
  );
};
